import React from 'react';
import PropTypes from 'prop-types';
import FlexBox from 'components/common/FlexBox';
import IconButton from 'components/common/buttons/IconButton';
import { Portal, TableCell } from '@mui/material';
import { ThemedHeaderTableCell } from 'components/common/table/TableStyles';
import { ResidualValueGridHeaderRow, ResidualValueGridTableRow } from '../../Styles';
import { get, sequence, set } from '@totalsoft/change-tracking-react';
import { useTranslation } from 'react-i18next';
import { TextField, Typography } from '@totalsoft/rocket-ui';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { valueOrDefault } from 'utils/functions';
import MaxHeightTable from 'components/common/MaxHeightTable';

const emptyDetail = () => ({
  id: crypto.randomUUID(),
  mileageFrom: null,
  mileageTo: null,
  period: null,
  rvPercent: null
});

const defaultActionsSlotId = 'default-detail-actions-slot';

const ResidualValueGridDetailsComponent = ({ lens, loading, actionsSlotId, validation, disabled }) => {
  const { t } = useTranslation();

  const addDetail = () => {
    const existingDetails = lens |> get;
    (lens |> set)([...existingDetails, emptyDetail()]);
  };

  const deleteDetail = id => () => {
    const details = lens |> get;
    (lens |> set)(details.filter(detail => detail.id !== id));
  };

  const hasDetails = (lens.length |> get) > 0;

  return (
    <FlexBox direction='column' gap={1}>
      {!actionsSlotId && <FlexBox gap={0.5} justifyContent='end' id={defaultActionsSlotId} />}
      <MaxHeightTable maxHeight={400}>
        <MaxHeightTable.Head>
          <ResidualValueGridHeaderRow>
            <ThemedHeaderTableCell>{t('ResidualValueGrid.MileageFrom')}</ThemedHeaderTableCell>
            <ThemedHeaderTableCell>{t('ResidualValueGrid.MileageTo')}</ThemedHeaderTableCell>
            <ThemedHeaderTableCell>{t('ResidualValueGrid.Period')}</ThemedHeaderTableCell>
            <ThemedHeaderTableCell>{t('ResidualValueGrid.RVPercent')}</ThemedHeaderTableCell>
            <ThemedHeaderTableCell width='75px'></ThemedHeaderTableCell>
          </ResidualValueGridHeaderRow>
        </MaxHeightTable.Head>
        <MaxHeightTable.Body>
          {loading && (
            <ResidualValueGridTableRow>
              <TableCell colSpan={5}>
                <WrappedFakeText lines={4} />
              </TableCell>
            </ResidualValueGridTableRow>
          )}
          {!loading && !hasDetails && (
            <ResidualValueGridTableRow>
              <TableCell colSpan={5} align='center'>
                <Typography>{t('TableIsEmpty')}</Typography>
              </TableCell>
            </ResidualValueGridTableRow>
          )}
          {!loading &&
            hasDetails &&
            (lens |> sequence).map((detailLens, index) => (
              <ResidualValueGridTableRow hover key={detailLens.id |> get}>
                <TableCell>
                  <TextField
                    fullWidth
                    isNumeric
                    decimalScale={2}
                    value={detailLens.mileageFrom |> get |> valueOrDefault('')}
                    onChange={detailLens.mileageFrom |> set}
                    error={!isValid(validation[index].mileageFrom)}
                    helperText={getErrors(validation[index].mileageFrom)}
                    disabled={disabled}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    isNumeric
                    decimalScale={2}
                    value={detailLens.mileageTo |> get |> valueOrDefault('')}
                    onChange={detailLens.mileageTo |> set}
                    error={!isValid(validation[index].mileageTo)}
                    helperText={getErrors(validation[index].mileageTo)}
                    disabled={disabled}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    isNumeric
                    decimalScale={0}
                    value={detailLens.period |> get |> valueOrDefault('')}
                    onChange={detailLens.period |> set}
                    error={!isValid(validation[index].period)}
                    helperText={getErrors(validation[index].period)}
                    disabled={disabled}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    isNumeric
                    decimalScale={4}
                    value={detailLens.rvPercent |> get |> valueOrDefault('')}
                    onChange={detailLens.rvPercent |> set}
                    error={!isValid(validation[index].rvPercent)}
                    helperText={getErrors(validation[index].rvPercent)}
                    disabled={disabled}
                  />
                </TableCell>
                <TableCell align='right'>
                  <IconButton type='delete' color='error' onClick={deleteDetail(detailLens.id |> get)} disabled={disabled} />
                </TableCell>
              </ResidualValueGridTableRow>
            ))}
        </MaxHeightTable.Body>
      </MaxHeightTable>
      <Portal container={() => document.getElementById(actionsSlotId || defaultActionsSlotId)}>
        <IconButton type='add' onClick={addDetail} disabled={disabled || loading} />
      </Portal>
    </FlexBox>
  );
};

ResidualValueGridDetailsComponent.propTypes = {
  lens: PropTypes.object,
  loading: PropTypes.bool,
  actionsSlotId: PropTypes.string,
  validation: PropTypes.object,
  disabled: PropTypes.bool
};

export default ResidualValueGridDetailsComponent;
